import { InfoOutlined } from '@mui/icons-material';
import { Box, SxProps, Popover, IconButton, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useState } from 'react';

type FooterProps = {
  onCancel?: () => void;
  onSecondaryClick?: () => void;
  onPrimaryClick?: () => void;
  isLoading?: boolean;
  primaryDisabled?: boolean;
  secondaryDisabled?: boolean;
  cancelText?: string;
  primaryText?: string;
  secondaryText?: string;
  hidePrimary?: boolean;
  hideCancel?: boolean;
  popoverInfoText?: string;
  customComponent?: React.ReactNode;
  primaryButtonId?: string;
  hideSecondary?: boolean;
};

const footerHeight = 50;

const container: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  borderTop: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  zIndex: 2,
  height: footerHeight
};

const Footer: React.FC<FooterProps> = ({
  onCancel,
  onSecondaryClick,
  onPrimaryClick,
  isLoading,
  primaryDisabled,
  cancelText,
  primaryText = 'Next',
  secondaryText = 'Save',
  secondaryDisabled,
  hidePrimary,
  hideSecondary,
  hideCancel,
  popoverInfoText,
  customComponent,
  primaryButtonId
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleStatusInfoClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleInfoStatusClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box height={footerHeight + 24} />
      <Box sx={container}>
        <Box>
          {onCancel && !hideCancel && (
            <Button variant="tertiary" onClick={onCancel}>
              {cancelText || 'Cancel'}
            </Button>
          )}
        </Box>
        <Box display="flex" alignItems="center">
          {customComponent && <Box mr={2}>{customComponent}</Box>}

          {popoverInfoText && (
            <IconButton onClick={(event) => handleStatusInfoClick(event)}>
              <InfoOutlined sx={{ color: 'info.main' }} />
            </IconButton>
          )}

          {!hidePrimary && (
            <Button
              variant="primary"
              onClick={onPrimaryClick}
              disabled={isLoading || primaryDisabled}
              id={primaryButtonId}
              sx={{ marginRight: 2 }}
            >
              {isLoading ? 'Loading...' : primaryText}
            </Button>
          )}

          {!hideSecondary && onSecondaryClick && (
            <Button
              variant="primary"
              onClick={onSecondaryClick}
              disabled={isLoading || secondaryDisabled}
            >
              {secondaryText}
            </Button>
          )}

          {popoverInfoText && (
            <Popover
              id="info-status-poppover"
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleInfoStatusClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <Typography sx={{ p: 1 }}>{popoverInfoText}</Typography>
            </Popover>
          )}
        </Box>
      </Box>
    </>
  );
};

export { Footer };
